import { useMemo, useState } from "react";
import { useOutletContext } from "react-router";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Button, Stack, Typography } from "@mui/material";
import { ClientContent, ClientTabBase } from "@shared";

import { ClientDetailResponse } from "@/api";
import { SearchField } from "@/common/components/search-field/SearchField";
import { ClientStatusWidget } from "@/pages/clients/client/components/client-status-widget/ClientStatusWidget";
import { NEW_SRV_PROCEEDING_ID } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

import { filterByFileNumber } from "./SrvProceedingTab.utils";
import { SrvProceedingDetail } from "./components/srv-proceeding-detail/SrvProceedingDetail";
import { SrvProceedingList } from "./components/srv-proceeding-list/SrvProceedingList";
import { useSrvProceedingSelection } from "./hooks/useSrvProceedingSelection";
import { useSrvProceedings } from "./hooks/useSrvProceedings";

const TRANSLATION_BASE = "clients.client.debtSettlement.srvProceeding";

export function SrvProceedingTab() {
  const client = useOutletContext<ClientDetailResponse>();
  const { t } = useTranslation();
  const { proceedings } = useSrvProceedings(client.clientId);
  const { isCreating, selectedProceeding, select, startCreate, cancelCreate, selectCreated } =
    useSrvProceedingSelection(proceedings);
  // Local rather than in the URL: typing a search term is no navigation the unsaved-changes guard should catch.
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const visibleProceedings = useMemo(() => filterByFileNumber(proceedings, searchTerm), [proceedings, searchTerm]);

  return (
    <ClientTabBase
      tabActions={
        <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={startCreate} disabled={isCreating}>
          {t(`${TRANSLATION_BASE}.create`)}
        </Button>
      }
      navigation={
        <Stack gap={2} overflow="hidden">
          <SearchField
            placeholder={t(`${TRANSLATION_BASE}.list.searchPlaceholder`)}
            onChange={setSearchTerm}
            width="100%"
          />
          <SrvProceedingList
            proceedings={visibleProceedings}
            selectedId={selectedProceeding?.id}
            isFiltered={!!searchTerm}
            onSelect={select}
          />
        </Stack>
      }
      navigationFooter={<ClientStatusWidget client={client} />}
    >
      {isCreating || selectedProceeding ? (
        <SrvProceedingDetail
          key={selectedProceeding?.id ?? NEW_SRV_PROCEEDING_ID}
          clientId={client.clientId}
          proceedingId={selectedProceeding?.id}
          onCreated={selectCreated}
          onCancelCreate={cancelCreate}
        />
      ) : (
        <ClientContent>
          <Stack bgcolor="background.paper" flex={1} p={2}>
            <Typography>{t(`${TRANSLATION_BASE}.emptyState`)}</Typography>
          </Stack>
        </ClientContent>
      )}
    </ClientTabBase>
  );
}
